import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCheckCircle, faSpinner, faDatabase } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import config from '../config';
import { useNiches } from '../hooks/useNiches';
import { getNicheIcon } from '../utils/nicheIcons';
import PublicSponsorCard, { PublicSponsorPreview } from '../components/PublicSponsorCard';
import { trackPageView } from '../utils/analytics';
import '../css/pages/NicheLanding.css';
import '../css/components/HomeNicheSearch.css';

interface NicheLandingProps {
    userAuth: boolean;
    isSubscribed: boolean;
    isAdmin?: boolean;
}

// The tag value meaning "no filter" — a URL segment rather than a separate
// route, so every footer/niche link and this page's own in-page filter
// chips are all just different :tag values into the same one page.
const ALL_SENTINEL = 'all';

// How many of the most recent placements (across every niche) this page
// fetches once and then filters/sorts entirely client-side — same
// fetch-once-then-filter pattern the real paid table uses. This is the
// "limited view" ceiling of the free database preview.
const PREVIEW_POOL_LIMIT = 40;

type SortMode = 'date' | 'audience';

// The public, filterable "solid gold" free preview of the database — one
// page for every case. Arriving via a specific link (a footer niche link, a
// homepage pill, a shared URL) auto-applies that niche as the starting
// filter, but it's a real filter: cleared or changed in-page without a
// reload, same as a subscriber filtering the real database.
//
// A subscriber (or admin) landing here shouldn't see a limited teaser of
// data they already have full access to — send them straight into the
// real, unlocked database instead.
const NicheLanding: React.FC<NicheLandingProps> = ({ userAuth, isSubscribed, isAdmin }) => {
    const { tag = '' } = useParams<{ tag: string }>();
    const navigate = useNavigate();
    const urlTag = decodeURIComponent(tag);
    const urlNiche = (!urlTag || urlTag.toLowerCase() === ALL_SENTINEL) ? null : urlTag;

    const [activeNiche, setActiveNiche] = useState<string | null>(urlNiche);
    const [sortMode, setSortMode] = useState<SortMode>('date');
    const [pool, setPool] = useState<PublicSponsorPreview[]>([]);
    const [loading, setLoading] = useState(true);
    const { niches } = useNiches();
    const searchableNiches = niches.filter((n) => n.freeSearch);

    // Re-sync if the user arrives at a different :tag via a direct link,
    // the footer, or the back button — a chip click below updates the URL
    // itself (see selectNiche), so this just keeps the two in agreement
    // regardless of which one changed first.
    useEffect(() => {
        setActiveNiche(urlNiche);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlTag]);

    useEffect(() => {
        trackPageView('NicheLanding', activeNiche ? `${activeNiche} Sponsors` : 'All Sponsors (free preview)');
    }, [activeNiche]);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        axios.get(`${config.backendUrl}sponsors/preview?limit=${PREVIEW_POOL_LIMIT}`).then((res) => {
            if (!mounted) return;
            setPool(res.data?.preview || []);
        }).catch(() => {
            if (mounted) setPool([]);
        }).finally(() => {
            if (mounted) setLoading(false);
        });
        return () => { mounted = false; };
    }, []);

    const filteredItems = useMemo(() => {
        const items = activeNiche
            ? pool.filter((item) => item.contentTags.some((t) => t.toLowerCase() === activeNiche.toLowerCase()))
            : pool;
        return [...items].sort((a, b) => (
            sortMode === 'audience'
                ? (b.audienceSize || 0) - (a.audienceSize || 0)
                : new Date(b.dateAdded || 0).getTime() - new Date(a.dateAdded || 0).getTime()
        ));
    }, [pool, activeNiche, sortMode]);

    if (userAuth && (isSubscribed || isAdmin)) {
        return <Navigate to={activeNiche ? `/sponsors?niche=${encodeURIComponent(activeNiche)}` : '/sponsors'} replace />;
    }

    const selectNiche = (nicheTag: string | null) => {
        setActiveNiche(nicheTag);
        navigate(`/niche/${nicheTag ? encodeURIComponent(nicheTag) : ALL_SENTINEL}`, { replace: true });
    };

    const pageTitle = activeNiche
        ? `${activeNiche} Newsletter Sponsors — Real Companies & Contacts | SponsorDB`
        : 'Free Newsletter Sponsor Database — Real Companies & Contacts | SponsorDB';
    const pageDescription = activeNiche
        ? `Browse real companies currently sponsoring ${activeNiche} newsletters, with verified contact info. Free preview, updated weekly as new sponsor placements are added.`
        : 'Browse and filter the most recently added real companies sponsoring newsletters, with verified contact info. A free, limited preview of the full database.';
    const canonicalUrl = `https://sponsor-db.com/niche/${encodeURIComponent(activeNiche || ALL_SENTINEL)}`;

    return (
        <div className="web-page niche-landing">
            {/* Open Graph and Twitter Card tags deliberately omitted here —
                social crawlers fetch plain HTML with no JS execution, so a
                Helmet-rendered override never reaches them anyway; see
                index.html for why those stay static and site-wide instead. */}
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <link rel="canonical" href={canonicalUrl} />
            </Helmet>
            <div className="web-section web-section-dark mt-0">
                <div className="web-section__container web-section-content">
                    <div className="niche-landing__hero">
                        <p className="niche-landing__eyebrow">Sponsor database</p>
                        <h1 className="niche-landing__title">
                            {activeNiche ? `${activeNiche} newsletter sponsors` : 'Free newsletter sponsor database'}
                        </h1>
                        <p className="niche-landing__subtitle">
                            {activeNiche
                                ? `Real companies currently sponsoring ${activeNiche} newsletters, with direct contact info — updated as new placements come in.`
                                : 'Real, recently-added sponsor placements across every niche. Filter and sort below — a free, limited preview of the full database.'}
                        </p>
                    </div>
                </div>
            </div>

            <div className="web-section mt-0">
                <div className="web-section__container web-section-content">
                    <div className="niche-landing__body niche-landing__body--wide">
                        <div className="niche-landing__controls">
                            <div className="home-niche-search__pills">
                                <button
                                    type="button"
                                    className={`home-niche-search__pill ${!activeNiche ? 'active' : ''}`}
                                    onClick={() => selectNiche(null)}
                                >
                                    <FontAwesomeIcon icon={faDatabase} /> All
                                </button>
                                {searchableNiches.map((n) => (
                                    <button
                                        key={n.tag}
                                        type="button"
                                        className={`home-niche-search__pill ${activeNiche?.toLowerCase() === n.tag.toLowerCase() ? 'active' : ''}`}
                                        onClick={() => selectNiche(n.tag)}
                                    >
                                        <FontAwesomeIcon icon={getNicheIcon(n.tag)} /> {n.tag}
                                    </button>
                                ))}
                            </div>
                            <div className="niche-landing__sort">
                                <span className="niche-landing__sort-label">Sort:</span>
                                <button
                                    type="button"
                                    className={`niche-landing__sort-btn ${sortMode === 'date' ? 'active' : ''}`}
                                    onClick={() => setSortMode('date')}
                                >
                                    Newest
                                </button>
                                <button
                                    type="button"
                                    className={`niche-landing__sort-btn ${sortMode === 'audience' ? 'active' : ''}`}
                                    onClick={() => setSortMode('audience')}
                                >
                                    Largest Audience
                                </button>
                            </div>
                        </div>

                        {loading ? (
                            <div className="niche-preview-list--loading">
                                <FontAwesomeIcon icon={faSpinner} spin /> Loading sponsors…
                            </div>
                        ) : filteredItems.length === 0 ? (
                            <div className="niche-preview-list--empty">
                                No {activeNiche ? `${activeNiche} ` : ''}sponsors in this free preview yet — check back soon, the database grows daily.
                            </div>
                        ) : (
                            <div className="placements-grid placements-grid--desktop">
                                {filteredItems.map((item, idx) => (
                                    <PublicSponsorCard key={`${item.sponsorName}-${idx}`} item={item} />
                                ))}
                            </div>
                        )}

                        <div className="niche-landing__cta">
                            <h2>{activeNiche ? `Want the full ${activeNiche} list?` : 'Want full access?'}</h2>
                            <p>
                                {activeNiche
                                    ? 'Subscribers get every sponsor in this niche, not just this free preview — plus verified contacts and AI-drafted pitches.'
                                    : 'Subscribers get the entire database, not just this free preview — plus verified contacts and AI-drafted pitches.'}
                            </p>
                            <div className="niche-landing__cta-actions">
                                <Link to="/signup" className="btn btn-primary">
                                    Get SponsorDB <FontAwesomeIcon icon={faArrowRight} />
                                </Link>
                            </div>
                            <ul className="niche-landing__trust">
                                <li><FontAwesomeIcon icon={faCheckCircle} /> Verified contact info</li>
                                <li><FontAwesomeIcon icon={faCheckCircle} /> $20/month, cancel anytime</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NicheLanding;
